import {
  CircularProgress, Table, TableBody, TableCell, TableFooter, TableRow,
} from '@mui/material';
import React from 'react';
import TablePagination from '@mui/material/TablePagination';
import { useTranslation } from 'react-i18next';
import { Account } from '../../../@types/Account';
import { TABLE } from '../../../consts';
import STRING_KEYS from '../../../language/keys';
import TableHeadContainer from '../../../components/Table/TableHead';
import { ACCOUNT_PARAMS } from '../../../consts/requestParamsKeys';
import { Sort } from '../../../interfaces/apiRequest.interface';
import { AccountsRowItem } from './AccountsRowItem';
import TalentsEmptyState from './AccountsEmptyState';
import AccountsCounter from './AccountsCounter';

type AccountsTableProps = {
  accountsData: TalentsListResponse;
  error: unknown | boolean;
  onPageChange: (newPageNumber: number) => void;
  isLoading: boolean;
  page: number;
  activeSort: Sort;
  onSortClick: (val: Sort) => void;
  onDeleteHandler: (accountId: string) => void,
  onEditAccount: (id: string) => void,
};

export const AccountsTable = ({
  accountsData,
  error,
  onPageChange,
  isLoading,
  page,
  activeSort,
  onSortClick,
  onDeleteHandler,
  onEditAccount,
}: AccountsTableProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const headers = [
    {
      key: ACCOUNT_PARAMS.DISPLAY_NAME,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_NAME_AND_ID),
      isSortable: true,
    },
    {
      key: ACCOUNT_PARAMS.ASSOCIATED_COMPANY,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_ASSOCIATED_COMPANY),
      isSortable: false,
    },
    {
      key: ACCOUNT_PARAMS.PLAN,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_PLAN),
      isSortable: false,
    },
    {
      key: ACCOUNT_PARAMS.STATUS,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_STATUS),
      isSortable: true,
    },
    {
      key: ACCOUNT_PARAMS.POSITIONS,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_POSITIONS),
      isSortable: false,
    },
    {
      key: ACCOUNT_PARAMS.DATE_UPDATED,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_LAST_UPDATE),
      isSortable: true,
    },
    {
      key: ACCOUNT_PARAMS.DATE_CREATED,
      value: translate(STRING_KEYS.ACCOUNTS_TABLE_HEADER_DATE_CREATED),
      isSortable: true,
    },
    {
      key: 'action',
      value: '',
    },
  ];

  const handleChangePage = (
    e: React.MouseEvent<HTMLButtonElement> | null,
    newPageNumber: number,
  ): void => {
    onPageChange(newPageNumber + 1);
  };

  // todo MATAN: common component
  const renderTalentCounter = (): JSX.Element => {
    if (!accountsData || accountsData.totalCount <= 0) {
      return <TalentsEmptyState tableColumnsCount={ headers.length } />;
    }

    return (
      <AccountsCounter
        total={ accountsData.totalCount }
        tableColumnsCount={ headers.length }
      />
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={ headers.length }>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    return accountsData?.results.map((account: Account) => {
      return (
        <AccountsRowItem
          key={ account.id }
          account={ account }
          onDeleteHandler={ onDeleteHandler }
          onEditAccountClick={ () => onEditAccount(account.id) }
        />
      );
    });
  };

  if (error) {
    return <div>{translate(STRING_KEYS.TALENTS_ERROR)}</div>;
  }

  return (
    <>
      <Table>
        <TableHeadContainer
          headers={ headers }
          order={ activeSort }
          orderHandler={ onSortClick }
        />

        <TableBody>
          {renderTalentCounter()}
          {renderTableBody()}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TablePagination
              count={ accountsData ? accountsData.totalCount : 0 }
              page={ page }
              rowsPerPage={ TABLE.ROWS_PER_PAGE }
              colSpan={ headers.length }
              onPageChange={ handleChangePage }
              rowsPerPageOptions={ [] }
            />
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
};
